'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useShoppingCart } from '@/ShoppingCart/useShoppingCart'
import { useStore } from '@/store'
import { useCookie } from '@/Cookies/useCookies'
import { BRAND_COOKIE, COUNTRY_COOKIE } from '@/lib/auth/marketCookies'
import {
  appendCheckoutMarketMoveQuery,
  navigateToMarketForBrand,
  parseRegionalBrandCode,
} from '@/lib/auth/domainSwitch'
import {
  getBrandCodeForHost,
  type RegionalBrandCode,
} from '@/lib/seo/marketUrls'
import {
  checkoutCountryIsoForRegionalMarket,
  resolveCheckoutRegionalBrand,
} from '@/lib/checkout/resolveCheckoutRegionalBrand'
import {
  regionalBrandDefaultCountryIso,
  UK_STOREFRONT_CURRENCY_ID,
  US_STOREFRONT_CURRENCY_ID,
} from '@/lib/checkout/regionalStoreDefaults'
import {
  SortEnumType,
  useCountriesQuery,
  useCurrenciesQuery,
} from '@/generated/graphql'
import { isEuropeanCountry } from '@/lib/middleware/variantRedirect.helper'

type UseRegionalMarketOptions = {
  /** When true, enables checkout domain switching. */
  enableCheckoutSwitch?: boolean
  /** When true, keeps header currency/country display aligned with the current storefront. */
  enableCurrencySync?: boolean
}

const CURRENCIES_STALE_MS = 86_400_000

async function resolveGeoCountry(signal: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch('/api/geo/', { signal })
    if (!response.ok) return null

    const data = (await response.json()) as { country?: string | null }
    const country = data.country?.trim().toUpperCase() || null
    return country && /^[A-Z]{2}$/.test(country) ? country : null
  } catch {
    return null
  }
}

/**
 * Coordinates regional storefront state. Checkout may redirect to the matching
 * regional host; non-checkout currency sync only repairs local display state.
 */
export function useRegionalMarket({
  enableCheckoutSwitch = false,
  enableCurrencySync = false,
}: UseRegionalMarketOptions = {}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hydrated = useStore(state => state.hydrated)
  const currency = useStore(state => state.currency)
  const country = useStore(state => state.country)
  const setCurrency = useStore(state => state.setCurrency)
  const setCountry = useStore(state => state.setCountry)
  const { getCookie, setCookie } = useCookie()
  const { cartData, isLoading: isCartLoading } = useShoppingCart({
    enabled: enableCheckoutSwitch,
  })

  const { data: countriesData } = useCountriesQuery(
    {
      order: { name: SortEnumType.Asc },
    },
    {
      enabled: enableCheckoutSwitch,
    }
  )

  const { data: currenciesData } = useCurrenciesQuery(undefined, {
    enabled: enableCurrencySync || enableCheckoutSwitch,
    staleTime: CURRENCIES_STALE_MS,
    retry: 2,
  })

  const [isProcessing, setIsProcessing] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const activeSwitchAttemptKeyRef = useRef<string | null>(null)
  const startedNavigationAttemptKeyRef = useRef<string | null>(null)

  const { usdId, gbpId } = useMemo(() => {
    const currencies = currenciesData?.currencies
    if (!currencies?.length) {
      return {
        usdId: US_STOREFRONT_CURRENCY_ID,
        gbpId: UK_STOREFRONT_CURRENCY_ID,
      }
    }

    let usdId: number | null = null
    let gbpId: number | null = null

    for (const c of currencies) {
      const code = c.code?.trim().toUpperCase()
      if (code === 'USD') usdId = c.id
      else if (code === 'GBP') gbpId = c.id
      else if (!usdId && c.symbol?.includes('$')) usdId = c.id
      else if (!gbpId && c.symbol?.includes('£')) gbpId = c.id
    }

    return {
      usdId: usdId ?? US_STOREFRONT_CURRENCY_ID,
      gbpId: gbpId ?? UK_STOREFRONT_CURRENCY_ID,
    }
  }, [currenciesData?.currencies])

  const hostBrand = useMemo(() => {
    if (typeof window === 'undefined') return null
    return getBrandCodeForHost(window.location.host)
  }, [])

  const cookieBrand = useMemo(() => {
    return parseRegionalBrandCode(getCookie(BRAND_COOKIE))
  }, [getCookie])

  const siteBrand: RegionalBrandCode | null = hostBrand ?? cookieBrand

  const requiredBrand = useMemo(() => {
    if (!enableCheckoutSwitch || !cartData?.items?.length) return null

    const countryIsoForMarket = checkoutCountryIsoForRegionalMarket(cartData)
    const result = resolveCheckoutRegionalBrand({
      countryIsoCode: countryIsoForMarket,
      cartBrandCode: cartData?.brand?.code,
      currencyCode: cartData?.currency?.code,
      countries: countriesData?.countries ?? [],
    })

    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.log('useRegionalMarket - requiredBrand calculation:', {
        countryIsoForMarket,
        cartBrandCode: cartData?.brand?.code,
        cartCurrency: cartData?.currency?.code?.trim().toUpperCase(),
        siteBrand,
        result,
        reason: countryIsoForMarket ? 'country-based' : 'fallback logic',
        cartCountry: cartData?.country?.isoCode,
        shippingCountry: cartData?.shippingAddress?.country?.isoCode,
      })
    }

    return result
  }, [enableCheckoutSwitch, cartData, countriesData?.countries, siteBrand])

  const syncStorefrontState = useCallback(
    async (signal: AbortSignal) => {
      if (!enableCurrencySync || !siteBrand || !hydrated) return

      const expectedCurrencyId =
        siteBrand === 'VANISHING_INC_UK' ? gbpId : usdId

      if (currency !== expectedCurrencyId) {
        setCurrency(expectedCurrencyId)
      }

      const normalizedCountry = country?.trim().toUpperCase()
      const countryMatchesHost =
        normalizedCountry &&
        (siteBrand === 'VANISHING_INC_UK'
          ? isEuropeanCountry(normalizedCountry)
          : !isEuropeanCountry(normalizedCountry))

      if (!countryMatchesHost) {
        const geoCountry = await resolveGeoCountry(signal)
        if (signal.aborted) return

        const geoCountryMatchesHost =
          geoCountry &&
          (siteBrand === 'VANISHING_INC_UK'
            ? isEuropeanCountry(geoCountry)
            : !isEuropeanCountry(geoCountry))
        const countryForStorefront = geoCountryMatchesHost
          ? geoCountry
          : regionalBrandDefaultCountryIso(siteBrand)

        setCountry(countryForStorefront)
        setCookie(COUNTRY_COOKIE, countryForStorefront)
      }
    },
    [
      enableCurrencySync,
      siteBrand,
      hydrated,
      currency,
      country,
      gbpId,
      usdId,
      setCurrency,
      setCountry,
      setCookie,
    ]
  )

  const handleCheckoutSwitch = useCallback(async () => {
    if (!hydrated || isCartLoading || isProcessing) return
    if (!requiredBrand || !siteBrand || requiredBrand === siteBrand) return
    if (!cartData?.items?.length) return
    if (pathname?.includes('/checkout/processing')) return

    try {
      const countryIsoForMarket = checkoutCountryIsoForRegionalMarket(cartData)
      const countryIso =
        countryIsoForMarket ||
        (requiredBrand === 'VANISHING_INC_UK' ? 'GB' : 'US')

      const search = searchParams.toString()
      const basePath = pathname?.trim() || '/checkout'
      const pathWithSearch = search ? `${basePath}?${search}` : basePath
      const redirectPath = appendCheckoutMarketMoveQuery(pathWithSearch)
      const switchAttemptKey = [
        requiredBrand,
        siteBrand,
        cartData?.token ?? '',
        countryIso,
        redirectPath,
      ].join('|')

      if (
        activeSwitchAttemptKeyRef.current === switchAttemptKey ||
        startedNavigationAttemptKeyRef.current === switchAttemptKey
      ) {
        return
      }

      activeSwitchAttemptKeyRef.current = switchAttemptKey

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsProcessing(true)

      const navigationStarted = await navigateToMarketForBrand({
        brandCode: requiredBrand,
        redirectPath,
        selectedCountryIsoCode: countryIso,
        shoppingCartToken: cartData?.token ?? null,
        onSameHost: async () => {},
      })

      if (abortController.signal.aborted) return

      if (navigationStarted) {
        startedNavigationAttemptKeyRef.current = switchAttemptKey
        return
      }

      if (activeSwitchAttemptKeyRef.current === switchAttemptKey) {
        activeSwitchAttemptKeyRef.current = null
      }
    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) return
      activeSwitchAttemptKeyRef.current = null
      console.error('Regional market switch failed:', error)
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsProcessing(false)
      }
    }
  }, [
    hydrated,
    isCartLoading,
    isProcessing,
    requiredBrand,
    siteBrand,
    cartData,
    pathname,
    searchParams,
  ])

  useEffect(() => {
    const abortController = new AbortController()
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) void syncStorefrontState(abortController.signal)
    })
    return () => {
      cancelled = true
      abortController.abort()
    }
  }, [syncStorefrontState])

  useEffect(() => {
    if (!enableCheckoutSwitch) return

    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) void handleCheckoutSwitch()
    })
    return () => {
      cancelled = true
    }
  }, [enableCheckoutSwitch, handleCheckoutSwitch])

  const needsSwitch = Boolean(
    requiredBrand && siteBrand && requiredBrand !== siteBrand
  )

  useEffect(() => {
    if (!needsSwitch) {
      activeSwitchAttemptKeyRef.current = null
      startedNavigationAttemptKeyRef.current = null
    }
  }, [needsSwitch])

  const isResolvingCheckoutSwitch = Boolean(
    enableCheckoutSwitch &&
    hydrated &&
    (isCartLoading || isProcessing || needsSwitch)
  )

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      activeSwitchAttemptKeyRef.current = null
      startedNavigationAttemptKeyRef.current = null
      setIsProcessing(false)
    }
  }, [])

  return {
    siteBrand,
    requiredBrand,
    isProcessing,
    needsSwitch,
    isResolvingCheckoutSwitch,
  }
}
